import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { MagicButton } from '@/features/agents/components/magic-button';
import { AgentFile } from '@/features/agents/hooks/use-export-agent';
import { useImportAgent } from '@/features/agents/hooks/use-import-agent';

export function UploadAgentForm() {
  const navigate = useNavigate();
  const previewMutation = useImportAgent();
  const uploadMutation = useImportAgent();
  const [agentFile, setAgentFile] = useState<AgentFile | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  async function handleFile(file: File | undefined) {
    if (!file) {
      return;
    }

    setFileName(file.name);
    setAgentFile(null);
    setFileError(null);
    previewMutation.reset();
    uploadMutation.reset();

    let parsed: AgentFile;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setFileError('This file is not valid JSON.');
      return;
    }

    if (parsed?.format !== 'dyta-agent' || !parsed.agent?.uuid) {
      setFileError('This is not a DYTA agent file.');
      return;
    }

    setAgentFile(parsed);
    previewMutation.mutate({ agentFile: parsed, dryRun: true });
  }

  function handleDrop(event: React.DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    handleFile(event.dataTransfer.files[0]);
  }

  function handleUpload() {
    if (!agentFile) {
      return;
    }

    uploadMutation.mutate(
      { agentFile },
      {
        onSuccess: (data) => navigate(`/agents/${data.uuid}/test`),
      }
    );
  }

  const preview = previewMutation.data;
  const error =
    fileError ||
    (previewMutation.error as Error | null)?.message ||
    (uploadMutation.error as Error | null)?.message;

  return (
    <div className="flex flex-col gap-y-4 max-w-md">
      <label
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={clsx(
          'flex flex-col items-center justify-center gap-y-2 rounded-lg border border-dashed px-6 py-8 text-center cursor-pointer shadow-sm',
          isDragging
            ? 'border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
            : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
        )}
      >
        <ArrowUpTrayIcon
          className={clsx(
            'h-6 w-6',
            isDragging
              ? 'text-indigo-600 dark:text-indigo-400'
              : 'text-gray-400 dark:text-gray-500'
          )}
        />
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {fileName || 'Drop an agent file here or click to choose one'}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          .agent.json files downloaded from the agent list
        </span>
        <input
          type="file"
          accept=".json,application/json"
          className="sr-only"
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </label>

      {previewMutation.isLoading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Checking file…
        </p>
      )}

      {preview && agentFile && (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4 text-sm">
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {preview.title}
          </p>
          <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">
            {preview.uuid}
          </p>
          {preview.files?.length ? (
            <p className="mt-2 font-mono text-xs text-gray-500 dark:text-gray-400">
              {preview.files.join(', ')}
            </p>
          ) : null}
          {preview.mode === 'overwrite' ? (
            <p className="mt-3 text-amber-700 dark:text-amber-400">
              This agent already exists
              {preview.existingTitle
                ? ` as “${preview.existingTitle}”`
                : ''}{' '}
              and will be overwritten, including its code.
            </p>
          ) : (
            <p className="mt-3 text-green-700 dark:text-green-400">
              A new agent will be created in your organisation.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <div className="flex justify-start">
        <MagicButton
          label={
            uploadMutation.isLoading
              ? 'Uploading…'
              : preview?.mode === 'overwrite'
              ? 'Overwrite'
              : 'Upload'
          }
          type="button"
          onClick={handleUpload}
          disabled={!preview || uploadMutation.isLoading}
        />
      </div>
    </div>
  );
}
